import { RestError } from '@azure/core-http';
import { UseMutationResult } from 'react-query';
import { useImmer } from 'use-immer';
import { parseErrorMessage } from '../../core/api/api.context';
import {
  ConfirmationModalProps,
  ConfirmationNote
} from '../../ui/components/pure/modal-content/confirmation-modal.component';

interface State {
  isLoading: boolean;
  errorMessage?: string;
}

const initialState: State = {
  isLoading: false
};

export function useConfirmationAction<T>({
  mutationHook,
  mutationArgs,
  onSuccess,
  modalCloseDelay,
  defaultErrorMessage,
  overrideErrorMessage
}: {
  mutationHook(): UseMutationResult<unknown, RestError, T>;
  mutationArgs: T | ((note?: ConfirmationNote) => T);
  onSuccess(): void;
  modalCloseDelay?: number;
  defaultErrorMessage?: string;
  overrideErrorMessage?: string;
}) {
  const useMutation = mutationHook;

  const [state, setState] = useImmer(initialState);

  const mutation = useMutation();

  const handleSuccess = () => {
    if (modalCloseDelay) {
      setTimeout(() => {
        setState((draft) => {
          onSuccess();
          draft.isLoading = false;
        });
      }, 300);
    } else {
      onSuccess();
    }
  };

  const handleError = (error: RestError) => {
    setState((draft) => {
      draft.isLoading = false;
      draft.errorMessage =
        overrideErrorMessage || parseErrorMessage(error.message) || defaultErrorMessage || 'Unknown error';
      try {
        const errorMessageObject = JSON.parse(draft.errorMessage);
        draft.errorMessage = errorMessageObject.message || draft.errorMessage;
      } catch (e) {
        return;
      }
    });
  };

  const onConfirm: ConfirmationModalProps['onConfirm'] = (note) => {
    setState((draft) => {
      draft.isLoading = true;
    });

    let args: T;

    if (mutationArgs instanceof Function) {
      args = mutationArgs(note);
    } else {
      args = mutationArgs;
    }

    mutation.mutate(args, {
      onSuccess() {
        handleSuccess();
      },
      onError(error) {
        handleError(error);
      }
    });
  };

  return { state, onConfirm } as const;
}
